import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CheckBox, Text } from 'grommet';
import PropTypes from 'prop-types';
import _ from 'lodash';

import QuiversPayApi from 'bigCommerce/gateways/bigCommerce-api';
import bigCommerce from 'common/gateways/common-api.js';
import FormField from 'granite-admin/core/components/FormField';
import Select from 'granite-admin/core/components/Select';
import InfoText from 'common/components/infoText';

const QuiversPay = ({ setComp, comp, errorToast, successToast, storeData, data, id, business, setData }) => {
  const [store, setStore] = useState({});
  const [channel, setChannel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quiversValue, setQuiversValue] = useState({});

  const savedMarketplaces = data?.settings?.dynamic?.quiversData?.marketplaces || [];

  const getChannelsForStore = storeId =>
    savedMarketplaces.filter(m => m.store_id === storeId);

  const channelOptions = getChannelsForStore(store?.id).map(m => ({
    ...m,
    label: m.channel_name || m.channel_id,
  }));

  // Auto-call postQuiversPay on page load with existing saved data
  const postExistingSettings = useCallback(
    async (storeId, storeChannels, fetchedValue) => {
      try {
        const stripeMap = {};
        const debugMap = {};
        storeChannels.forEach(ch => {
          stripeMap[ch.channel_id] = fetchedValue?.stripe?.[ch.channel_id] ?? 0;
          debugMap[ch.channel_id] = fetchedValue?.debug_mode?.[ch.channel_id] ?? 0;
        });
        await QuiversPayApi.postQuiversPay({
          store_hash: storeId,
          sandbox_mode: 0,
          paypal: 0,
          paymentkeys: {
            paypalClientId: data?.settings?.static?.paypalClientId || '',
            paypalClientSecret: data?.settings?.static?.paypalClientSecret || '',
            stripePublicKey: data?.settings?.static?.stripePublicKey || '',
          },
          marketplaces: storeChannels,
          stripe: stripeMap,
          debug_mode: debugMap,
        });
      } catch (e) {
        // silent — page visit sync, don't show error to user
      }
    },
    [data],
  );

  const fetchQuivers = useCallback(
    async storeId => {
      try {
        const res = await QuiversPayApi.getQuiversPay(storeId);
        const fetched = res?.data || {};
        setQuiversValue(fetched);
        // Auto-sync existing settings on page visit
        const storeChannels = savedMarketplaces.filter(m => m.store_id === storeId);
        await postExistingSettings(storeId, storeChannels, fetched);
      } catch (e) {
        errorToast(e.errors?.title || 'Failed to fetch record');
      }
    },
    [errorToast, postExistingSettings, savedMarketplaces],
  );

  // stripe/debug_mode per channel from fetched data
  const getChannelSettings = ch => ({
    stripe: quiversValue?.stripe?.[ch?.channel_id] ?? 0,
    debug_mode: quiversValue?.debug_mode?.[ch?.channel_id] ?? 0,
  });

  const channelSaved = channel ? getChannelSettings(channel) : { stripe: 0, debug_mode: 0 };

  const [localValues, setLocalValues] = useState({ stripe: 0, debug_mode: 0 });

  useEffect(() => {
    setLocalValues(channelSaved);
  }, [channel, quiversValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (storeData?.length > 0) {
      setStore(storeData[0]);
      fetchQuivers(storeData[0]?.id);
      const chs = getChannelsForStore(storeData[0]?.id);
      if (chs.length > 0) setChannel(chs[0]);
    }
  }, [storeData]); // eslint-disable-line react-hooks/exhaustive-deps

  const isUnchanged = _.isEqual(localValues, channelSaved);

  const onSubmit = async () => {
    if (!channel) return;
    setSubmitting(true);
    try {
      // All channel entries for the selected store
      const storeChannels = getChannelsForStore(store.id);

      // Build stripe and debug_mode channel maps
      // Merge existing fetched values for all channels, override only current channel
      const stripeMap = {};
      const debugMap = {};
      storeChannels.forEach(ch => {
        stripeMap[ch.channel_id] = ch.channel_id === channel.channel_id
          ? localValues.stripe
          : (quiversValue?.stripe?.[ch.channel_id] ?? 0);
        debugMap[ch.channel_id] = ch.channel_id === channel.channel_id
          ? localValues.debug_mode
          : (quiversValue?.debug_mode?.[ch.channel_id] ?? 0);
      });

      // Call external QuiversPay API with store+channel wise stripe/debug_mode
      const externalPayload = {
        store_hash: store.id,
        sandbox_mode: 0,
        paypal: 0,
        paymentkeys: {
          paypalClientId: data?.settings?.static?.paypalClientId || '',
          paypalClientSecret: data?.settings?.static?.paypalClientSecret || '',
          stripePublicKey: data?.settings?.static?.stripePublicKey || '',
        },
        marketplaces: storeChannels,
        stripe: stripeMap,
        debug_mode: debugMap,
      };
      await QuiversPayApi.postQuiversPay(externalPayload);

      const { quiversPaySettings: _removed, store_name_mapping: oldMapping, ...quiversDataRest } = data?.settings?.dynamic?.quiversData || {};

      // Rebuild store_name_mapping keeping only nested object entries (store hashes)
      const cleanStoreNameMapping = {};
      Object.entries(oldMapping || {}).forEach(([key, val]) => {
        if (typeof val === 'object' && val !== null)
          cleanStoreNameMapping[key] = val;
      });

      const payload = {
        id,
        settings: {
          ...data?.settings?.dynamic,
          quiversData: {
            ...quiversDataRest,
            store_name_mapping: cleanStoreNameMapping,
          },
        },
        displayName: data?.displayName,
      };

      await bigCommerce.updateData({ business, payload });
      const res = await bigCommerce.getData({ id, business });
      setData(res);
      successToast('Data updated successfully');
    } catch (e) {
      errorToast(e.errors?.title || 'Failed to Update Data');
    }
    setSubmitting(false);
  };

  return (
    <Box width={'70%'} pad={{ top: 'large' }} border={{ color: 'lightgray', size: 'xsmall', side: 'top' }}>
      <Box pad={{ bottom: 'large' }}>
        <Text size="xlarge" margin={{ bottom: 'medium' }} weight="bold">
          Select the store for which you want to configure Quivers Pay and customize the settings as per your needs
        </Text>

        <Box direction="row" gap="medium" margin={{ bottom: 'medium' }}>
          <Box width="48%">
            <Text margin={{ bottom: 'small' }} weight={'bold'}>Select a Store</Text>
            <FormField name="id" noBorder width={'100%'}>
              <Box border="1px solid black">
                <Select
                  options={storeData || []}
                  name="id"
                  onChange={e => {
                    if (e?.value) {
                      setStore(e.value);
                      fetchQuivers(e.value.id);
                      const chs = getChannelsForStore(e.value.id);
                      setChannel(chs.length > 0 ? chs[0] : null);
                    } else {
                      setStore({});
                      setChannel(null);
                      setQuiversValue({});
                    }
                  }}
                  value={store?.id}
                  valueKey={{ key: 'id', reduce: false }}
                  labelKey="label"
                />
              </Box>
            </FormField>
          </Box>

          <Box width="48%">
            <Text margin={{ bottom: 'small' }} weight={'bold'}>Select a Channel</Text>
            <FormField name="channel" noBorder width={'100%'}>
              <Box border="1px solid black">
                <Select
                  options={channelOptions}
                  name="channel"
                  placeholder="Select Channel"
                  onChange={e => setChannel(e?.value || null)}
                  value={channel?.channel_id || ''}
                  valueKey={{ key: 'channel_id', reduce: false }}
                  labelKey="label"
                  valueLabel={
                    channel
                      ? <Box pad={{ horizontal: 'small', vertical: 'xsmall' }}><Text>{channel.channel_name || channel.channel_id}</Text></Box>
                      : undefined
                  }
                  disabled={!store?.id}
                />
              </Box>
            </FormField>
          </Box>
        </Box>
      </Box>

      <Box margin={{ bottom: 'large' }}>
        <Text size="xlarge" margin={{ bottom: 'medium' }} weight="bold">
          Configuration Settings
        </Text>
        <Box
          justify="between"
          direction="row"
          margin={{ bottom: 'large' }}
          border={{ color: 'lightgray', size: 'xsmall', side: 'bottom' }}
        >
          <Box width={'50%'} margin={{ bottom: 'medium' }} border={{ color: 'lightgray', size: 'xsmall', side: 'right' }}>
            <Box direction="row" gap="small" margin={{ bottom: 'small' }}>
              <Text weight="bold">Stripe</Text>
              <InfoText text="Hide and show Quivers stripe payment method on checkout." />
            </Box>
            <CheckBox
              name="stripe"
              toggle
              reverse
              disabled={!channel}
              checked={!!localValues.stripe}
              onChange={e => setLocalValues(prev => ({ ...prev, stripe: e.target.checked ? 1 : 0 }))}
            />
          </Box>

          <Box width={'46%'}>
            <Box direction="row" gap="small" margin={{ bottom: 'small' }}>
              <Text weight="bold">Debug</Text>
              <InfoText text="Enable debug mode and click on save button to generate logs on Quivers end." />
            </Box>
            <CheckBox
              name="debug_mode"
              toggle
              reverse
              disabled={!channel}
              checked={!!localValues.debug_mode}
              onChange={e => setLocalValues(prev => ({ ...prev, debug_mode: e.target.checked ? 1 : 0 }))}
            />
          </Box>
        </Box>
      </Box>

      <Box justify="end" gap="xlarge" direction="row" margin={{ vertical: 'large' }}>
        <Box align="end">
          <Button label={'Back'} primary onClick={() => setComp(comp - 1)} />
        </Box>
        <Box align="end">
          {isUnchanged ? (
            <Button label={'Next'} primary onClick={() => setComp(comp + 1)} />
          ) : (
            <Button label={submitting ? 'Saving...' : 'Save'} primary onClick={onSubmit} disabled={!channel || submitting} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

QuiversPay.propTypes = {
  setComp: PropTypes.func,
  errorToast: PropTypes.func,
  successToast: PropTypes.func,
  comp: PropTypes.number,
  data: PropTypes.object,
  id: PropTypes.string,
  setData: PropTypes.func,
  storeData: PropTypes.array,
};

export default QuiversPay;
